/**
 * Date utility functions to replace Luxon DateTime functionality
 * Uses native JavaScript Date objects for better bundle size
 */

/**
 * Parse an ISO date string to a Date object
 */
/**
 * CalendarDate utilities for Nuxt UI date inputs
 */
import { CalendarDate } from "@internationalized/date";

export function parseISO(isoString: string | null | undefined): Date | null {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Format a date as ISO date string (YYYY-MM-DD)
 */
export function toISODate(date: Date | null): string | null {
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Determine whether a given date occurs before the start of the current week.
 * Week starts on Monday (Sunday is treated as day 7).
 */
export function isOlderThanCurrentWeek(d: string | number | Date): boolean {
  const x = new Date(d);
  if (isNaN(x.getTime())) throw new Error("Invalid date, senpai!!");

  const n = new Date();
  const w = new Date(n);
  w.setHours(0, 0, 0, 0);
  w.setDate(n.getDate() + (n.getDay() === 0 ? -6 : 1 - n.getDay()));

  return x < w;
}

/**
 * Check whether a given date falls within the current week.
 * Week starts on Monday (Sunday treated as day 7).
 */
export function isWithinCurrentWeek(d: string | number | Date): boolean {
  const x = new Date(d);
  if (isNaN(x.getTime())) throw new Error("Invalid date, senpai!!");

  const n = new Date();
  const weekStart = new Date(n);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(n.getDate() + (n.getDay() === 0 ? -6 : 1 - n.getDay()));

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);

  return x >= weekStart && x < weekEnd;
}

/**
 * Format a date using a format string
 * Supports: DDD (full date), MMM (short month), dd MMM yyyy, etc.
 */
export function formatDate(date: Date | null, format: string): string {
  if (!date || isNaN(date.getTime())) return "";

  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthNamesLong = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayNamesLong = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  let result = format;

  // DDD format: "Mon, Jan 15, 2024"
  if (format === "DDD") {
    return `${dayNames[date.getDay()]}, ${monthNamesLong[month]} ${day}, ${year}`;
  }

  // dd MMM yyyy format: "15 Jan 2024"
  if (format === "dd MMM yyyy") {
    return `${String(day).padStart(2, "0")} ${monthNames[month]} ${year}`;
  }

  // MMM format: "Jan"
  if (format === "MMM") {
    return monthNames[month];
  }

  // Generic replacements
  result = result.replace(/yyyy/g, String(year));
  result = result.replace(/MMM/g, monthNames[month]);
  result = result.replace(/MMMM/g, monthNamesLong[month]);
  result = result.replace(/dd/g, String(day).padStart(2, "0"));
  result = result.replace(/d/g, String(day));
  result = result.replace(
    /DDD/g,
    `${dayNames[date.getDay()]}, ${monthNamesLong[month]} ${day}, ${year}`,
  );
  result = result.replace(/EEE/g, dayNames[date.getDay()]);
  result = result.replace(/EEEE/g, dayNamesLong[date.getDay()]);

  return result;
}

export const formatDateToString = (date: Date | null): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Convert date string to Date object (handles timezone issues)
export const parseDateString = (dateString: string | null | Date | undefined): Date | null => {
  if (!dateString) return null;
  if (typeof dateString === "object" && dateString instanceof Date) return dateString;
  const str = String(dateString);
  const parts = str.split("T")[0].split("-");
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return new Date(str);
};

/**
 * Get the start of a day (00:00:00.000)
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get the end of a day (23:59:59.999)
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get the start of a week (Monday)
 */
export function startOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
  result.setDate(diff);
  return startOfDay(result);
}

/**
 * Get the end of a week (Sunday)
 */
export function endOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? 0 : 7); // Adjust to Sunday
  result.setDate(diff);
  return endOfDay(result);
}

/**
 * Add time to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

export function addYears(date: Date, years: number): Date {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

/**
 * Calculate the difference between two dates
 */
export function diffInDays(start: Date, end: Date): number {
  const diffMs = end.getTime() - start.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function diffInMilliseconds(start: Date, end: Date): number {
  return end.getTime() - start.getTime();
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date | null, date2: Date | null): boolean {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if a date is valid
 */
export function isValid(date: Date | null): boolean {
  return date !== null && !isNaN(date.getTime());
}

/**
 * Get current date/time
 */
export function now(): Date {
  return new Date();
}

/**
 * Date picker specific utilities
 */

/**
 * Check if a date has a non-default time (not 00:00:00)
 */
export function hasTimeComponent(value: Date | string | null): boolean {
  const date = parseToDate(value);
  if (!date) return false;

  // Check if it's exactly midnight UTC (date-only value)
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  const milliseconds = date.getUTCMilliseconds();

  // If it's midnight UTC, treat it as date-only (no time)
  if (hours === 0 && minutes === 0 && seconds === 0 && milliseconds === 0) {
    return false;
  }

  return true;
}

/**
 * Parse Date or string to Date object safely
 */
export function parseToDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    date = new Date(value);
  } else {
    return null;
  }

  return isNaN(date.getTime()) ? null : date;
}

export function formatReportDate(date: string | Date): string {
  if (!date) return "-";
  const d = new Date(date);
  return d.toISOString().slice(0, 16).replace("T", "_").replace(/:/g, "-");
}

/**
 * Format date for picker display with special cases for Today/Tomorrow
 */
export function formatDateForPicker(
  value: Date | string | null,
  includeTime: boolean = false,
): string {
  const date = parseToDate(value);
  if (!date) return "";

  // The date object already represents the correct moment in time
  // JavaScript Date methods like getDate(), getMonth() etc. automatically
  // return values in the local timezone

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Check for today/tomorrow
  if (isSameDay(date, today)) {
    return includeTime ? `Today ${formatTimeSimple(date)}` : "Today";
  }
  if (isSameDay(date, tomorrow)) {
    return includeTime ? `Tomorrow ${formatTimeSimple(date)}` : "Tomorrow";
  }

  // Regular formatting
  const currentYear = new Date().getFullYear();
  const dateYear = date.getFullYear();

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const month = monthNames[date.getMonth()];
  const day = date.getDate();

  let dateStr = `${day} ${month}`;
  if (dateYear !== currentYear) {
    dateStr += `, ${dateYear}`;
  }

  if (includeTime) {
    dateStr += ` ${formatTimeSimple(date)}`;
  }

  return dateStr;
}

/**
 * Format time in 24-hour format
 */
export function formatTimeSimple(value: Date | string | null): string {
  const date = parseToDate(value);
  if (!date) return "";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Format a date and time together for display.
 * Example: "7 Jul 2026 at 14:30"
 */
export function formatDateTime(value: Date | string | null): string {
  const date = parseToDate(value);
  if (!date) return "—";
  return `${formatDate(date, "dd MMM yyyy")} at ${formatTimeSimple(date)}`;
}

/**
 * Check if a date is before today (midnight comparison)
 */
export function isBeforeToday(value: Date | string | null): boolean {
  const date = parseToDate(value);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);

  return compareDate < today;
}

/**
 * Extract hours and minutes from a date
 */
export function extractTime(
  value: Date | string | null,
): { hours: number; minutes: number } | null {
  const date = parseToDate(value);
  if (!date) return null;

  return {
    hours: date.getHours(),
    minutes: date.getMinutes(),
  };
}

/**
 * Convert ISO date string (YYYY-MM-DD) to CalendarDate
 * Returns null for invalid/empty values
 */
export function parseToCalendarDate(dateStr: string | null | undefined): CalendarDate | null {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return null;
  return new CalendarDate(year, month, day);
}

/**
 * Convert CalendarDate to ISO date string (YYYY-MM-DD)
 * Returns undefined for null values (for optional API fields)
 */
export function calendarDateToString(date: CalendarDate | null): string | undefined {
  if (!date) return undefined;
  return date.toString();
}
